import { __ } from '@oat-sa-private/ui-core';

/**
 * Returns object with translated keys. This keys are the
 * cases for dynamic translations, for example we can
 *  use dynamicKeys()[`${error}-error-solved`]
 * instead of handling each case on consumer
 * @param {Object} config - Config object for interpolation
 * @returns {Object} - Translated keys
 */
export default (config = {}) => ({
    // errors (notification content)
    'nrps-error-solved': __('Test taker information up to date.'),
    'ws-error-solved': __('Test taker data up to date.'),
    'internetConnection-error-solved': __('Internet connected.'),
    'interactions-error-solved': __('Test session list up to date.'),
    // ACS reports (notification content)
    'acs-single-authorize-success': __('succeeded'),
    'acs-multiple-authorize-success': __('succeeded'),
    'acs-single-authorize-fail': __('failed'),
    'acs-multiple-authorize-fail': __('failed'),
    'acs-single-report-success': __('succeeded'),
    'acs-multiple-report-success': __('succeeded'),
    'acs-single-report-fail': __('failed'),
    'acs-multiple-report-fail': __('failed'),
    'acs-single-pause-success': __('succeeded'),
    'acs-multiple-pause-success': __('succeeded'),
    'acs-single-pause-fail': __('failed'),
    'acs-multiple-pause-fail': __('failed'),
    'acs-single-resume-success': __('succeeded'),
    'acs-multiple-resume-success': __('succeeded'),
    'acs-single-resume-fail': __('failed'),
    'acs-multiple-resume-fail': __('failed'),
    'acs-single-terminate-success': __('succeeded'),
    'acs-multiple-terminate-success': __('succeeded'),
    'acs-single-terminate-fail': __('failed'),
    'acs-multiple-terminate-fail': __('failed'),
    'acs-single-reopen-success': __('succeeded'),
    'acs-multiple-reopen-success': __('succeeded'),
    'acs-single-reopen-fail': __('failed'),
    'acs-multiple-reopen-fail': __('failed'),
    'acs-single-extendTime-success': __('succeeded'),
    'acs-multiple-extendTime-success': __('succeeded'),
    'acs-single-extendTime-fail': __('failed'),
    'acs-multiple-extendTime-fail': __('failed'),
    'acs-single-resetExtraTime-success': __('succeeded'),
    'acs-multiple-resetExtraTime-success': __('succeeded'),
    'acs-single-resetExtraTime-fail': __('failed'),
    'acs-multiple-resetExtraTime-fail': __('failed'),
    'acs-single-reset-success': __('succeeded'),
    'acs-multiple-reset-success': __('succeeded'),
    'acs-single-reset-fail': __('failed'),
    'acs-multiple-reset-fail': __('failed'),
    // ACS button labels
    'acs-authorize-label-singular': __('Authorize'),
    'acs-authorize-label-plural': __('Authorize'),
    'acs-report-label-singular': __('Report'),
    'acs-report-label-plural': __('Report'),
    'acs-pause-label-singular': __('Pause'),
    'acs-pause-label-plural': __('Pause'),
    'acs-resume-label-singular': __('Resume'),
    'acs-resume-label-plural': __('Resume'),
    'acs-terminate-label-singular': __('Terminate'),
    'acs-terminate-label-plural': __('Terminate'),
    'acs-reopen-label-singular': __('Reopen'),
    'acs-reopen-label-plural': __('Reopen'),
    'acs-extend_time-label-singular': __('Extend time'),
    'acs-extend_time-label-plural': __('Extend time'),
    'acs-reset_extra_time-label-singular': __('Remove additional time'),
    'acs-reset_extra_time-label-plural': __('Remove additional time'),
    'acs-reset-label-singular': __('Reset'),
    'acs-reset-label-plural': __('Reset'),

    // dialog headings
    'dialog-pause-singular-header': __('Do you want to proceed pausing the test for test taker %s?', config.userName),
    'dialog-pause-plural-header': __('Do you want to proceed pausing these %d tests?', config.length),
    'dialog-terminate-singular-header': __(
        'Do you want to proceed with the termination of the test for test taker %s?',
        config.userName
    ),
    'dialog-terminate-plural-header': __(
        'Do you want to proceed with the termination of these %d tests?',
        config.length
    ),
    'dialog-reopen-singular-header': __(
        'Do you want to proceed with the reopening of the test for test taker %s?',
        config.userName
    ),
    'dialog-reopen-plural-header': __('Do you want to proceed with the reopening of these %d tests?', config.length),
    'dialog-extend_time-singular-header': __(
        'Do you want to proceed extending the time for test taker %s?',
        config.userName
    ),
    'dialog-extend_time-plural-header': __(
        'Do you want to proceed extending the time for these %d tests?',
        config.length
    ),
    'dialog-reset_extra_time-singular-header': __('Remove additional time for test taker: %s', config.userName),
    'dialog-reset_extra_time-plural-header': __('Remove additional time for %d test takers', config.length),
    'dialog-reset-plural-header': __('Do you want to reset these %d sessions?', config.length),
    'dialog-reset-singular-header': __('Do you want to reset the session for test taker %s?', config.userName),

    // dialog message
    'dialog-reset_extra_time-singular-message': __(
        'Keep in mind that any additional time given before the session has started will not be removed.'
    ),
    'dialog-reset_extra_time-plural-message': __(
        'Keep in mind that any additional time given before the session has started will not be removed.'
    ),

    // dialog buttons
    'dialog-pause-singular-button': __('Yes, pause test'),
    'dialog-pause-plural-button': __('Yes, pause %d tests', config.length),
    'dialog-terminate-singular-button': __('Yes, terminate test'),
    'dialog-terminate-plural-button': __('Yes, terminate %d tests', config.length),
    'dialog-reopen-singular-button': __('Yes, reopen test'),
    'dialog-reopen-plural-button': __('Yes, reopen %d tests', config.length),
    'dialog-extend_time-singular-button': __('Yes, extend time'),
    'dialog-extend_time-plural-button': __('Yes, extend time', config.length),
    'dialog-reset_extra_time-singular-button': __('Remove additional time'),
    'dialog-reset_extra_time-plural-button': __('Remove additional time', config.length),
    'dialog-reset-singular-button': __('Yes, reset the session'),
    'dialog-reset-plural-button': __('Yes, reset the sessions', config.length),
    'dialog-termresetinate-plural-button': __('Yes, reset %d sessions', config.length),

    // form headings
    'form-report-singular-header': __('Report test taker: %s', config.userName),
    'form-report-plural-header': __('Report test takers (%d)', config.length),
    'form-pause-singular-header': __('Pause test for test taker: %s', config.userName),
    'form-pause-plural-header': __('Pause test for test takers (%d)', config.length),
    'form-resume-singular-header': __('Resume test for test taker: %s', config.userName),
    'form-resume-plural-header': __('Resume test for test taker (%d)', config.length),
    'form-terminate-singular-header': __('Terminate session for test taker: %s', config.userName),
    'form-terminate-plural-header': __('Terminate sessions (%d)', config.length),
    'form-reopen-singular-header': __('Reopen session for test taker: %s', config.userName),
    'form-reopen-plural-header': __('Reopen sessions (%d)', config.length),
    'form-extend_time-singular-header': __('Extend time for test taker: %s', config.userName),
    'form-extend_time-plural-header': __('Extend time for test takers (%d)', config.length),
    'form-reset_extra_time-singular-header': __('Remove additional time for test taker: %s', config.userName),
    'form-reset_extra_time-plural-header': __('Remove additional time for %d test takers', config.length),
    'form-reset-singular-header': __('Reset session for test taker: %s', config.userName),
    'form-reset-plural-header': __('Reset sessions (%d)', config.length),
    // form button labels
    'form-report-singular-button': __('Report'),
    'form-report-plural-button': __('Report'),
    'form-pause-singular-button': __('Pause test'),
    'form-pause-plural-button': __('Pause tests'),
    'form-resume-singular-button': __('Resume test'),
    'form-resume-plural-button': __('Resume tests'),
    'form-terminate-singular-button': __('Terminate'),
    'form-terminate-plural-button': __('Terminate'),
    'form-reopen-singular-button': __('Reopen'),
    'form-reopen-plural-button': __('Reopen'),
    'form-extend_time-singular-button': __('Extend time'),
    'form-extend_time-plural-button': __('Extend time'),
    'form-reset_extra_time-singular-button': __('Remove additional time'),
    'form-reset_extra_time-plural-button': __('Remove additional time'),
    'form-reset-singular-button': __('Reset'),
    'form-reset-plural-button': __('Reset'),
    // table cell labels
    'status-initial-label': __('Awaiting authorization'),
    'status-authorized-label': __('Authorized'),
    'status-interacting-label': __('Interacting'),
    'status-suspended-label': __('Paused'),
    'status-terminated-label': __('Terminated by proctor'),
    'status-closed-label': __('Examinee submission'),
    'status-canceled-label': __('Canceled'),
    'status-unknown-label': __('Unknown')
});

/**
 * Some LDS have keys that need to be translated,
 * when running locale script this keys will
 * be added to .pot file to be handled
 * like the rest of app keys
 * @returns {Object} - LDS translation keys
 */
export function setLdsTranslations() {
    return {
        ALL: __('All'),
        SELECTED: __('selected')
    };
}
